import type { Thread } from "@brookbush/database";

import clsx from "clsx";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

import ConfirmationModal from "../ConfirmationModal";
import ChatHistoryItemMenu from "./ChatHistoryItemMenu";
import ChatRenameDialog from "./ChatRenameDialog";
import useGlobalStore from "~/hooks/useGlobalStore";
import useThreads from "~/hooks/useThreads";

interface ChatHistoryItemProps {
	thread: Thread;
	onSelect: () => void;
}

const ChatHistoryItem = ({ thread, onSelect }: ChatHistoryItemProps) => {
	const { activeChatThreadId, setActiveChatThreadId, closeChatBar } =
		useGlobalStore();
	const { archiveThread, renameThread } = useThreads(true);

	const [isRenaming, setIsRenaming] = useState(false);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

	const isActive = thread.id === activeChatThreadId;

	const handleDelete = async () => {
		await archiveThread(thread.id);
		if (isActive) {
			setActiveChatThreadId(null);
			closeChatBar();
		}
	};

	return (
		<>
			<div
				className={clsx(
					"group relative flex items-center gap-1 rounded-xl px-3 py-2 transition-colors",
					isActive
						? "bg-sky-50 dark:bg-sky-500/10"
						: "hover:bg-slate-100 dark:hover:bg-slate-800/70",
				)}
			>
				{isActive && (
					<span className="absolute top-1/2 left-0 h-6 w-1 -translate-y-1/2 rounded-r-full bg-sky-500" />
				)}

				<button
					type="button"
					onClick={onSelect}
					className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
				>
					<span
						className={clsx(
							"w-full truncate text-sm",
							isActive
								? "font-semibold text-sky-700 dark:text-sky-300"
								: "font-medium text-slate-700 dark:text-slate-200",
						)}
					>
						{thread.title}
					</span>
					<span className="truncate text-slate-400 text-xs dark:text-slate-500">
						{formatDistanceToNow(new Date(thread.updatedAt), {
							addSuffix: true,
						})}
					</span>
				</button>

				<div
					className={clsx(
						"shrink-0 transition-opacity",
						isActive
							? "opacity-100"
							: "opacity-100 tablet:opacity-0 tablet:focus-within:opacity-100 tablet:group-hover:opacity-100",
					)}
				>
					<ChatHistoryItemMenu
						onRename={() => setIsRenaming(true)}
						onDelete={() => setIsConfirmingDelete(true)}
					/>
				</div>
			</div>

			<ChatRenameDialog
				open={isRenaming}
				currentTitle={thread.title}
				onClose={() => setIsRenaming(false)}
				onRename={(title) => renameThread(thread.id, title)}
			/>

			<ConfirmationModal
				open={isConfirmingDelete}
				elevated
				title="Delete conversation"
				description={`Delete "${thread.title}"? This can't be undone.`}
				confirmationLabel="Delete"
				onClose={() => setIsConfirmingDelete(false)}
				onConfirm={async () => {
					await handleDelete();
					setIsConfirmingDelete(false);
				}}
			/>
		</>
	);
};

export default ChatHistoryItem;
